/**
 * What to tell the cook when a household action fails, as a translation key.
 *
 * The Share screen used to print `err.message`, so a failure read as whatever
 * the code that threw happened to say: « Household hh_… does not exist », in
 * English, with a developer's id in it (#776). A HouseholdError's message is
 * for logs; its `code` is the reason, and the reason is what gets a sentence.
 *
 * Duck-typed on `code` so this stays a pure function, testable without the
 * Firebase SDK that householdService imports.
 */

export type HouseholdAction = 'invite' | 'revoke' | 'leave';

const FALLBACK: Record<HouseholdAction, string> = {
  invite: 'household.errGeneric',
  revoke: 'household.errRevoke',
  leave: 'household.errLeave',
};

const BY_CODE: Record<string, string> = {
  network: 'household.errNetwork',
  'auth-required': 'household.errSessionExpired',
  'session-changed': 'household.errSessionChanged',
  'household-full': 'household.errFull',
  'not-in-household': 'household.errNotInHousehold',
};

export const householdErrorKey = (err: unknown, action: HouseholdAction): string => {
  const code =
    err && typeof err === 'object' && (err as { name?: unknown }).name === 'HouseholdError'
      ? (err as { code?: unknown }).code
      : undefined;
  if (typeof code !== 'string') return FALLBACK[action];
  // Only creating a household moves data, so only an invitation can fail that way.
  if (code === 'migration-failed' && action === 'invite') return 'household.errMoveFailed';
  return BY_CODE[code] ?? FALLBACK[action];
};
